import React, { useCallback } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { PopoverOrigin } from '@mui/material/Popover';

import { contextMenuZIndex, jwPlayerModalZIndex } from '~/styles';

import { MenuContainer } from './elements';
import { MenuProvider } from './MenuProvider';

export interface MenuProps {
  title?: React.ReactNode | string;
  getMenuItems: (closeMenu: () => void) => React.ReactNode[];
  iconButtonStyle?: React.CSSProperties;
  paperStyle?: React.CSSProperties;
  transformOrigin?: PopoverOrigin;
  iconStyle?: React.CSSProperties;
  bodyTestId?: string;
  iconTestId?: string;
  onClick?(): void;
  buttonLabel?: string;
  playerMode?: boolean;
}

export const Menu = React.forwardRef<HTMLButtonElement, MenuProps>(
  (
    {
      getMenuItems,
      iconStyle,
      paperStyle = { maxWidth: '25ch', width: 'fit-content' },
      transformOrigin = { vertical: 'top', horizontal: 'left' },
      iconButtonStyle,
      title,
      bodyTestId,
      iconTestId,
      onClick,
      buttonLabel,
      playerMode = false,
      ...restProps
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      if (onClick) onClick();
    };

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setAnchorEl(null);
    };

    const closeMenu = useCallback(() => {
      setAnchorEl(null);
    }, []);
    const menuItems = getMenuItems(closeMenu);
    if (menuItems.length === 0) {
      return null;
    }

    return (
      <MenuProvider onClose={closeMenu}>
        <div onClick={e => e.stopPropagation()}>
          <IconButton
            aria-label={buttonLabel ?? 'menu button'}
            aria-controls="long-menu"
            aria-haspopup="true"
            onClick={handleClick}
            data-testid={!iconTestId ? 'more-menu-icon' : iconTestId}
            style={{ padding: 0, ...iconButtonStyle }}
            size="large"
            ref={ref}
            {...restProps}
          >
            {title || <MoreVertIcon style={{ color: 'white', ...iconStyle }} />}
          </IconButton>
          {anchorEl && (
            <MenuContainer
              data-testid={!bodyTestId ? 'more-menu' : bodyTestId}
              id="long-menu"
              anchorEl={anchorEl}
              keepMounted
              open={true}
              onClose={handleClose}
              transformOrigin={transformOrigin}
              style={{ zIndex: playerMode ? jwPlayerModalZIndex + 5 : contextMenuZIndex }}
              PaperProps={{
                style: paperStyle,
              }}
            >
              {menuItems}
            </MenuContainer>
          )}
        </div>
      </MenuProvider>
    );
  },
);
